import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { CloseIcon } from "../../../../minimals/theme/overrides/CustomIcons";
import NotesTimeline from "../../../checks/checkNotes/NotesTimeline";
import { getNotes, saveAssignToInternalUser, saveNote } from "../../../checks/checkNotes/NotesUtils";
import { PriorityChip } from "../../../checks/PriorityStatus";
import InternalUserAutocomplete from "../../../shared/InternalUserAutocomplete";
import { InternalUser } from "../../../types/app";
import { DashboardReportType } from "../../../types/reports";
import { fCurrency } from "../../../utils/formatNumber";
import { IAssignable, Policy } from "./types";

interface IProps {
  policy: Policy;
  setPolicy: Function;
  setEntityTable?: Function;
  isOpen: boolean;
  orderNumberDisplay?: string;
  amount?: string;
  priority?: string;
  nameBeforeUpdate: string;
  emailBeforeUpdate: string;
  refetchData: Function;
  reportType: DashboardReportType;
  saveEscrowLedgerAssignTo?: (userName: string) => Promise<void>;
  saveEscrowLedgerNote?: (note: string) => Promise<void>;
  issueDropdown?: boolean;
  setOpenNotesIssuesModal: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function NotesAndIssuesModal({
  policy,
  setPolicy,
  setEntityTable,
  orderNumberDisplay,
  amount,
  priority,
  isOpen,
  nameBeforeUpdate = "",
  emailBeforeUpdate = "",
  refetchData,
  reportType,
  issueDropdown,
  setOpenNotesIssuesModal
}: IProps) {
  const [submitting, setSubmitting] = useState(false);
  const [text, setText] = useState<string>("");
  const [selectedAssignedTo, setSelectedAssignedTo] = useState<InternalUser | null>({
    fullName: nameBeforeUpdate || "",
    email: emailBeforeUpdate || ""
  } as InternalUser);
  const [issue, setIssue] = useState<{ value: number; label: string } | null>(null);
  const [closer, setCloser] = useState<InternalUser | null>(null);
  const [proforma, setProforma] = useState<InternalUser | null>(null);

  const policyIssuesOptions = [
    { value: 1, label: "Unresolved" },
    { value: 2, label: "Resolved" },
    { value: 3, label: "Written Off" }
  ];
  useEffect(() => {
    const selectedIssue = policyIssuesOptions.find((option) => option.value === policy.issue);
    setIssue(selectedIssue || null);
  }, [policy.issue]);

  const saveAssignTo = async (user: InternalUser | null) => {
    if (!user || user.email?.toLowerCase() == emailBeforeUpdate?.toLowerCase()) return;
    else {
      await saveAssignToInternalUser({
        uniqueIdentifier: policy.uniqueIdentifier,
        orderNumber: policy.orderNumber,
        user,
        reportType
      });
    }
    await updateSelectedEntity();
  };

  const onChangeIssue = async (policy: Policy) => {
    await axios.post("/api/outstandingChecks/UpdatePolicy", {
      ...policy,
      issue: issue?.value,
      assignedToUser: selectedAssignedTo
    });
  };

  const updateSelectedEntity = async () => {
    if (!policy) return;
    const notes = await getNotes({
      orderNumber: policy.orderNumber,
      uniqueIdentifier: policy.uniqueIdentifier,
      reportType
    });
    const updatedEntity = {
      ...policy,
      notes,
      assignedToUser: selectedAssignedTo,
      assignedToEmail: selectedAssignedTo?.email,
      assignedToName: selectedAssignedTo?.fullName,
      assignedTo: selectedAssignedTo?.fullName,
      issue: policy.issue
    };
    setPolicy(updatedEntity);
    if (setEntityTable) {
      setEntityTable((tableData: IAssignable[]) => {
        return tableData.map((row) => (row.uniqueIdentifier == policy.uniqueIdentifier ? updatedEntity : row));
      });
    }
    setText("");
  };
  const getPolicyUsers = async () => {
    const { data } = await axios.get(
      `/api/clientphonebook/internalusers/getInternalUsersByType?internalRoleTypeId=${8}`
    );
    return data;
  };
  const handleReplyToPolicyTeam = async (
    notesArray: {
      text: string;
      createdByUser: string;
    }[]
  ) => {
    const policyTeamUsers = await getPolicyUsers();
    for (let i = 0; i < notesArray.length; i++) {
      const note = notesArray[i];
      const match = note.text.match(/Assigned to (.+)/i);
      if (match) {
        const assignedName = match[1].trim()?.toLowerCase();
        const matchedUser = policyTeamUsers.find((user: any) => user.fullName?.toLowerCase().trim() === assignedName);
        if (matchedUser?.email) {
          setSelectedAssignedTo(matchedUser);
          await saveAssignTo(matchedUser);
          save();
          return;
        }
      }
    }
    const { data } = await axios.get(
      `/api/clientphonebook/internalusers/getInternalUserByEmail?email=${policy.createdBy}`
    );
    setSelectedAssignedTo(data);
    await saveAssignTo(data);
    save();
  };

  const getCloserAndProformaOfOrder = async () => {
    const { data } = await axios.get(
      `/api/outstandingChecks/GetCloserAndProformaOfOrder?orderNumber=${policy.orderNumber}`
    );
    setCloser(data.closerUser);
    setProforma(data.proformaUser);
  };

  const handleReplyToCloser = async () => {
    const { data } = await axios.get(
      `/api/clientphonebook/internalusers/getInternalUserByEmail?email=${closer?.email}`
    );
    setSelectedAssignedTo(data);
    await saveAssignTo(data);
    save();
  };

  const handleReplyToProforma = async () => {
    const { data } = await axios.get(
      `/api/clientphonebook/internalusers/getInternalUserByEmail?email=${proforma?.email}`
    );
    setSelectedAssignedTo(data);
    await saveAssignTo(data);
    save();
  };

  const handleReplyToLastCommenter = async (
    notesArray: {
      text: string;
      createdByUserEmail: string;
    }[]
  ) => {
    for (let i = 0; i < notesArray.length; i++) {
      const note = notesArray[i];
      const isSystemNote = note.text.startsWith("Assigned to ") || note.text.startsWith("Issue status updated");
      if (!isSystemNote) {
        const { data } = await axios.get(
          `/api/clientphonebook/internalusers/getInternalUserByEmail?email=${note.createdByUserEmail}`
        );
        setSelectedAssignedTo(data);
        await saveAssignTo(data);
        save();
        return;
      }
    }
  };

  const save = async () => {
    if (!(selectedAssignedTo?.email || text || issue)) return;
    setSubmitting(true);
    await saveAssignTo(selectedAssignedTo);
    await onChangeIssue(policy);
    await saveNote({
      uniqueIdentifier: policy.uniqueIdentifier,
      orderNumber: policy.orderNumber,
      text,
      reportType
    });
    await updateSelectedEntity();
    setSubmitting(false);
    refetchData();
  };

  useEffect(() => {
    const fetchAssignedToUser = async () => {
      if (selectedAssignedTo?.email) {
        const { data } = await axios.get(
          `/api/clientphonebook/internalusers/getInternalUserByEmail?email=${selectedAssignedTo?.email}`
        );
        if (data) {
          setSelectedAssignedTo(data);
        }
      }
    };
    fetchAssignedToUser();
  }, []);
  useEffect(() => {
    getCloserAndProformaOfOrder();
  }, []);
  const onClose = () => {
    setPolicy(null);
    setOpenNotesIssuesModal(false);
  };

  return (
    <Dialog open={isOpen} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center" marginBottom={3}>
          <Box>
            <Stack>
            Order #{orderNumberDisplay || policy.orderNumber}
            {amount ? ` - ${fCurrency(amount)}` : ""}
            </Stack>
            <Stack spacing={2} alignItems="center" direction="row">
            {+policy.type === 2 ? "Loan " : "Owners "} {policy.position && policy.position}
             {` Policy #${policy.policyNumber}`}
            </Stack>
          </Box>
          {priority && (
            <Box>
              <PriorityChip priority={priority} />
            </Box>
          )}
        </Stack>
        <Stack direction={"row"} justifyContent="space-between">
          <InternalUserAutocomplete
            sx={{ width: 220 }}
            value={selectedAssignedTo}
            onChange={(val) => {
              setSelectedAssignedTo(val as InternalUser | null);
            }}
            label="Assigned to"
          />
          {issueDropdown && (
            <Autocomplete
              options={policyIssuesOptions || []}
              getOptionLabel={(option) => option.label || ""}
              renderInput={(params) => <TextField {...params} label="Issue" />}
              onChange={(e, value) => {
                setIssue(value);
              }}
              // value={
              //   issue
              //     ? policyIssuesOptions.find((option) => option.value === issue?.value) || null
              //     : null
              // }
              value={issue}
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              sx={{ width: 220 }}
              size="small"
            />
          )}
        </Stack>
      </DialogTitle>
      <IconButton
        onClick={onClose}
        sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ paddingBottom: 3 }}>
        <Box paddingTop={1}>
          <TextField
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
            multiline
            rows={3}
            label="Note"
            sx={{ marginBottom: 2 }}
          />
          <Stack direction="row" spacing={2} rowGap={2} flexWrap="wrap" justifyContent="center" paddingBottom="24px">
            {closer && (
              <Button variant="soft" size="small" onClick={handleReplyToCloser}>
                Reply to Closer
              </Button>
            )}
            {proforma && (
              <Button variant="soft" size="small" onClick={handleReplyToProforma}>
                Reply to Pro Forma
              </Button>
            )}
            {policy.issue && policy.notes?.some((note) => note.text.includes("Issue status updated to Unresolved")) && (
              <Button variant="soft" size="small" onClick={() => handleReplyToPolicyTeam(policy?.notes)}>
                Reply to Policy Team
              </Button>
            )}
            {policy.notes?.length > 0 && (
              <Button variant="soft" size="small" onClick={() => handleReplyToLastCommenter(policy?.notes)}>
                Reply to last commenter
              </Button>
            )}
          </Stack>
          <Stack direction="row" justifyContent="right">
            <Button variant="contained" size="small" onClick={save} disabled={submitting}>
              {submitting ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </Box>
        <NotesTimeline notes={policy?.notes || []} />
      </DialogContent>
    </Dialog>
  );
}
